import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Portal } from 'react-portal'
import noop from 'lodash/noop'
import { useDimensions } from '../../../hooks/index.js'

const PosterZoomContext = createContext({
  onItemMouseMove: noop,
  onItemMouseOut: noop,
  setZoomBounds: noop,
})

export const PosterZoomProvider = ({ children, renderZoomedPanel, portalNode }) => {
  const { width, height } = useDimensions()
  const zoomBounds = useRef({ x: 0, y: 0, width, height })

  // When moving from one item to the next, the previous item zooms out
  // before the next one zooms in, so the next active item is queued
  const [activeItem, setActiveItem] = useState(null)
  const [nextActiveItem, setNextActiveItem] = useState(null)

  // Use the value in a callback without creating a new memo value
  const nextItemRefForMemo = useRef(nextActiveItem)
  useEffect(() => {
    nextItemRefForMemo.current = nextActiveItem
  }, [nextActiveItem])

  // While scrolling the placement of the item to zoom changes constantly, so a next
  // item will only be displayed once the user hasn't scrolled for a short delay.
  const disableNextItem = useRef(undefined)
  useEffect(() => {
    const onScroll = () => {
      clearTimeout(disableNextItem.current)
      disableNextItem.current = setTimeout(() => {
        disableNextItem.current = undefined
      }, 300)
    }
    document?.addEventListener('scroll', onScroll)
    return () => document?.removeEventListener('scroll', onScroll)
  }, [])

  // A short delay before the new active item zooms in
  useEffect(() => {
    if (!activeItem && nextActiveItem) {
      const timeout = setTimeout(() => {
        setActiveItem(nextActiveItem)
        setNextActiveItem(null)
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [activeItem, nextActiveItem])

  const value = useMemo(
    () => ({
      onItemMouseMove: (ev, item, position) => {
        if (!disableNextItem.current) {
          setNextActiveItem((old) =>
            old?.slug === item.slug
              ? old
              : {
                  item,
                  // The position of the hovered item, the zoom panel will be positioned on top
                  position,
                  // Zoomed items must remain inside this container
                  bounds: zoomBounds.current,
                },
          )
        }
      },
      onItemMouseOut: () => {
        if (nextItemRefForMemo.current) {
          setNextActiveItem(null)
        }
      },
      setZoomBounds: (bounds) => (zoomBounds.current = bounds),
    }),
    [],
  )

  return (
    <>
      {/* Callbacks passed via context rather than render prop, so children don't render when the item changes */}
      <PosterZoomContext.Provider value={value}>{children}</PosterZoomContext.Provider>

      {activeItem && (
        <Portal node={portalNode}>
          {renderZoomedPanel([activeItem, () => setActiveItem(null)])}
        </Portal>
      )}
    </>
  )
}

export const usePosterZoom = () => useContext(PosterZoomContext)
